import './operationcreator.css';
import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import CropSelector from '../CropSelector/CropSelector';
import ModelSelector from '../ModelSelector/ModelSelector';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/ToastContext/ToastContext';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { ServiceNames } from '../../Api/registry/Services';
import { fetchPerson } from '../../store/slices/personSlice';

const initialFormState = {
    fromDate: '',
    toDate: '',
    selectedCrop: null,
    modelConfigurations: []
};

const OperationCreator = ({ show, onHide, parcel }) => {
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { t } = useTranslation();
    const { showToast } = useToast();    
    const dispatch = useDispatch();

    // Reset state when modal is closed
    useEffect(() => {
        if (!show) {
            setFormData(initialFormState);
            setError(null);
            setSaving(false);
        }
    }, [show, parcel?.id]);

    const handleModelConfigured = (configurations) => {
        setFormData(prev => ({
            ...prev,
            modelConfigurations: configurations
        }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setError(null);

            // Trasforma le configurazioni dei modelli in OperationSettingRequest[]
            const settings = formData.modelConfigurations.map(config => {
                const weatherDataSettings = [{
                    meteoVariable: null,
                    stationLatitude: config.weatherConfig?.station?.latitude || null,
                    stationLongitude: config.weatherConfig?.station?.longitude || null,
                    stationName: config.weatherConfig?.station?.name || null
                }];

                return {
                    id: null,
                    modelReference: config.modelId,
                    modelParameter: config.parameters || {},
                    weatherDataSettings
                };
            });

            // Formatta le date in ISO 8601 con timezone offset, impostando l'ora a mezzanotte
            const formatDate = (dateStr) => {
                const date = new Date(dateStr);
                date.setHours(0, 0, 0, 0);
                return date.toISOString();
            };

            const operationData = {
                id: null,
                fromDate: formatDate(formData.fromDate),
                toDate: formatDate(formData.toDate),
                parcelReference: parcel.id,
                cropReference: formData.selectedCrop?.id,
                settings
            };

            const userService = await ServiceFactory.getService(ServiceNames.UserService);
            await userService.saveParcelOperation(operationData);
            await dispatch(fetchPerson());
            showToast(t("common:messages.success"),"success");
            onHide();
        } catch (err) {
            showToast(err.message || t("error:wrong") ,"error");
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        // Se cambia il crop, resetta le configurazioni dei modelli
        if (field === 'selectedCrop') {
            setFormData(prev => ({ 
                ...prev, 
                [field]: value,
                modelConfigurations: [] 
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const isBasicInfoValid = formData.fromDate && formData.toDate && formData.selectedCrop;
    const hasConfiguredModels = formData.modelConfigurations.length > 0;

    return (
        <Modal 
            show={show} 
            onHide={onHide}
            className="operation-creator-modal"
            backdrop="static"
            keyboard={false}
            dialogClassName="operation-creator-dialog"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("operationcreator:labels.createOperation")} {parcel?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>                
                <div className="operation-creator-content">
                    <div className="basic-info-section">
                        <div className="row g-2">
                            <div className="col-md-3">
                                <Form.Group>
                                    <Form.Label>{t("operationcreator:labels.fromDate")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.fromDate || ''}
                                        onChange={(e) => handleInputChange('fromDate', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group>
                                    <Form.Label>{t("operationcreator:labels.toDate")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.toDate || ''}
                                        onChange={(e) => handleInputChange('toDate', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>{t("operationcreator:labels.selectCrop")}</Form.Label>
                                    <CropSelector
                                        selectedCrop={formData.selectedCrop}
                                        onSelect={(crop) => handleInputChange('selectedCrop', crop)}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </div>

                    {isBasicInfoValid && (
                        <div className="model-selector-section">
                            <ModelSelector
                                selectedCrop={formData.selectedCrop}
                                parcelPosition={{ lat: parcel.latitude, lon: parcel.longitude }}
                                onModelConfigured={handleModelConfigured}
                                onEditingChange={setIsEditing}
                            />
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    {t("operationcreator:buttons.cancel")}
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={!hasConfiguredModels || saving || isEditing}
                >
                    {saving ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            {t("operationcreator:buttons.creating")}
                        </>
                    ) : (
                        'Create Operation'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default OperationCreator;